import { copyPath, deletePath, growPath, inlinePath, makePath } from '@ck';
import { point, type Matrix, type Path, type Point, type Rect } from '@geom';
import { canvasKit } from '@resources';
import type { Path as CKPath, PathOp as CKPathOp } from 'canvaskit';
import { toCKPath } from './drawHelpers';
import { PathOp } from './PathOp';
import { type StrokeOpts } from './StrokeOpts';
import { toCkStrokeOpts } from './visualUtils';

export class Shape {
  #ckPath: CKPath | undefined;

  static #emptyShape: Readonly<Shape> | undefined;

  constructor(ckPath: CKPath) {
    this.#ckPath = ckPath;
  }

  static fromPath(path: Path): Shape {
    return new Shape(toCKPath(path));
  }

  static get EMPTY(): Readonly<Shape> {
    if (!Shape.#emptyShape) {
      Shape.#emptyShape = Object.freeze(new Shape(makePath()));
    }
    return Shape.#emptyShape;
  }

  get ckPath(): CKPath {
    if (this.#ckPath === undefined) {
      throw new Error('Path already deleted.');
    }
    if (this.#ckPath.isDeleted()) {
      throw new Error('Path in Shape deleted.');
    }
    return this.#ckPath;
  }

  get svgPath(): string {
    return this.ckPath.toSVGString();
  }

  delete() {
    if (Object.isFrozen(this)) {
      throw new Error('Cannot delete frozen shape.');
    }
    deletePath(this.ckPath);
    this.#ckPath = undefined;
  }

  inline(width: number): Shape {
    return new Shape(inlinePath(this.ckPath, width));
  }

  copy(): Shape {
    return new Shape(copyPath(this.ckPath));
  }

  trim(startT: number, stopT: number, isComplement: boolean): Shape {
    const trimmedPath = copyPath(this.ckPath).trim(startT, stopT, isComplement);
    if (!trimmedPath) {
      throw new Error('Failed to trim path.');
    }
    return new Shape(trimmedPath);
  }

  stroke(opts?: Partial<StrokeOpts>): Shape {
    const strokePath = copyPath(this.ckPath).stroke(toCkStrokeOpts(opts));
    if (!strokePath) {
      throw new Error('Failed to stroke path.');
    }
    return new Shape(strokePath);
  }

  countPoints(): number {
    return this.ckPath.countPoints();
  }

  getPoint(index: number): Point {
    return point(this.ckPath.getPoint(index));
  }

  transform(matrix: Matrix): Shape {
    const transformedPath = copyPath(this.ckPath).transform(matrix.elements);
    return new Shape(transformedPath);
  }

  op(other: Shape, op: PathOp): Shape {
    let ckPathOp: CKPathOp;
    switch (op) {
      case PathOp.DIFFERENCE:
        ckPathOp = canvasKit.PathOp.Difference;
        break;
      case PathOp.INTERSECT:
        ckPathOp = canvasKit.PathOp.Intersect;
        break;
      case PathOp.UNION:
        ckPathOp = canvasKit.PathOp.Union;
        break;
      case PathOp.XOR:
        ckPathOp = canvasKit.PathOp.XOR;
        break;
      case PathOp.REVERSE_DIFFERENCE:
        ckPathOp = canvasKit.PathOp.ReverseDifference;
        break;
      default:
        throw new Error('Invalid path operation.');
    }
    const resultPath = copyPath(this.ckPath);
    const success = resultPath.op(other.ckPath, ckPathOp);
    if (!success) {
      throw new Error('Failed to perform path operation.');
    }
    return new Shape(resultPath);
  }

  grow(width: number): Shape {
    return new Shape(growPath(this.ckPath, width));
  }
}

export class ShapeBuilder {
  #ckPath: CKPath | undefined;

  constructor() {
    this.#ckPath = makePath();
  }

  private get ckPath(): CKPath {
    if (this.#ckPath === undefined) {
      throw new Error('Path already deleted.');
    }
    if (this.#ckPath.isDeleted()) {
      throw new Error('Path in ShapeBuilder deleted.');
    }
    return this.#ckPath;
  }

  delete() {
    deletePath(this.ckPath);
    this.#ckPath = undefined;
  }

  build(): Shape {
    const pth = this.ckPath;
    this.#ckPath = undefined;
    return new Shape(pth);
  }

  moveTo(p: Point): this {
    this.ckPath.moveTo(p.x, p.y);
    return this;
  }

  lineTo(p: Point): this {
    this.ckPath.lineTo(p.x, p.y);
    return this;
  }

  close(): this {
    this.ckPath.close();
    return this;
  }

  addRect(rect: Rect, isCCW?: boolean): this {
    this.ckPath.addRect(rect.array, isCCW);
    return this;
  }

  addOval(rect: Rect, isCCW?: boolean): this {
    this.ckPath.addOval(rect.array, isCCW);
    return this;
  }
}
